import { colors } from '../styles/tokens';
import { slaColor, tasaColor } from './format';

/**
 * GLOSARIO DEL TABLERO: un nombre por métrica, el mismo en Global, Tienda y la
 * app (SPEC `2026-10-12-tableros-homologados.md`, «Glosario»).
 *
 * Hasta el 12-oct la misma métrica tenía hasta tres nombres —«Consultas
 * totales» / «Consultas», «SLA bodeguero» / «Atendidas por Bodeguero» /
 * «Atendidas»— y, peor, el MISMO nombre tapaba fórmulas distintas: «Tasa de
 * atención» era V/A en Global y V/S en Tienda (49,5 % contra 46,6 % para VAZ 56
 * en la S40). Todo rótulo de métrica sale de aquí; si una pantalla necesita
 * otro texto, lo que falta es una entrada en este archivo.
 */
export interface DefinicionMetrica {
  /** Rótulo visible. */
  rotulo: string;
  /** Fórmula con las letras del embudo (C, S, A, N, V), si es un cociente. */
  formula?: string;
  /** Ayuda corta (el ícono «i» o la sub-línea). */
  nota?: string;
}

export type MetricaId =
  | 'consultas'
  | 'solicitudes'
  | 'atendidas'
  | 'negados'
  | 'venta'
  | 'efectividad'
  | 'atendidasBodega'
  | 'pctAtendidas'
  | 'tasa'
  | 'ventaContado'
  | 'ventaCredito'
  | 'ventaTotal'
  | 'descuento'
  | 'prestamos'
  | 'clientesNuevos'
  | 'consultasAtendidas';

/**
 * ⚠️ La app lleva una COPIA EXACTA de este glosario (`cdr-sa-app/src/features/
 * indicadores/utils/glosario.ts`): mismas llaves, rótulos, fórmulas y notas.
 * `glosario.test.ts` de los dos repos compara contra la MISMA tabla fija
 * (revisión cruzada del 12-oct): si cambias un texto aquí, cópialo allá (y a la
 * tabla de las dos pruebas).
 */
export const GLOSARIO: Record<MetricaId, DefinicionMetrica> = {
  consultas: { rotulo: 'Consultas', nota: 'Consultas de disponibilidad' },
  solicitudes: { rotulo: 'Solicitudes a bodega', nota: 'Piezas pedidas a bodega' },
  atendidas: { rotulo: 'Solicitudes atendidas', nota: 'El bodeguero entregó' },
  negados: { rotulo: 'Negados', nota: 'Talla solicitada sin existencia' },
  venta: { rotulo: 'Venta (pares)', nota: 'Pares de calzado' },
  efectividad: {
    rotulo: 'Efectividad de inventario',
    formula: 'S ÷ C',
    nota: 'Consultas que terminaron en solicitud',
  },
  atendidasBodega: {
    rotulo: 'Atendidas por bodeguero',
    formula: 'A ÷ S',
    nota: 'Solicitudes que bodega surtió',
  },
  /** Columna del % A ÷ S en la tabla de Global: «Atendidas» ya es el conteo. */
  pctAtendidas: { rotulo: '% atendidas', formula: 'A ÷ S', nota: 'Solicitudes que bodega surtió' },
  tasa: {
    rotulo: 'Tasa de atención',
    formula: 'V ÷ A',
    nota:
      'Pares vendidos ÷ solicitudes atendidas. Idealmente ≤ 100 %: toda la venta pasa por el ' +
      'sistema. Un valor mayor indica venta fuera del sistema.',
  },
  ventaContado: { rotulo: 'Venta contado' },
  ventaCredito: { rotulo: 'Venta crédito' },
  ventaTotal: { rotulo: 'Venta total' },
  descuento: { rotulo: 'Descuento concedido' },
  prestamos: { rotulo: 'Préstamos personales', nota: 'No es venta comercial' },
  clientesNuevos: { rotulo: 'Clientes nuevos', nota: 'Estrenaron su crédito (regla B)' },
  /**
   * Tarjeta del VENDEDOR contra una API anterior al 12-oct, que no manda
   * `solicitudes` por vendedor: su porcentaje es A ÷ C, así que no puede
   * llamarse «Efectividad de inventario», que es S ÷ C. Regla del spec: con
   * otra fórmula, otro nombre. Con `solicitudes` (la API ya las manda), la
   * tarjeta usa `efectividad`, la misma S ÷ C de la tienda y de Global.
   */
  consultasAtendidas: {
    rotulo: 'Consultas atendidas',
    formula: 'A ÷ C',
    nota: 'Solicitudes atendidas ÷ consultas: qué tan seguido había la talla en bodega cuando se pidió.',
  },
};

export type MetricaConSemaforo = 'efectividad' | 'atendidasBodega' | 'tasa';

/**
 * UNA función de semáforo por métrica, con los umbrales vigentes de Global.
 * Recibe la FRACCIÓN (0..1, como la manda la API), no el porcentaje.
 *
 * - Efectividad de inventario: sin semáforo (el color de marca, como en Global).
 * - Atendidas por bodeguero (A ÷ S): ≥ 95 % verde, ≥ 90 % dorado, si no rojo.
 *   Antes había tres juegos de umbrales para la misma cifra: verde fijo en el
 *   medidor, 95/90 en la tabla y 93/88 en la tarjeta de bodega.
 * - Tasa de atención (V ÷ A): INVERTIDO — ≤ 100 % verde, ≤ 200 % dorado, si no
 *   rojo. Alto es malo: es venta que no pasó por la herramienta.
 */
export function semaforo(metrica: MetricaConSemaforo, fraccion: number | null | undefined): string {
  const pct = fraccion === null || fraccion === undefined || Number.isNaN(fraccion) ? null : fraccion * 100;
  switch (metrica) {
    case 'efectividad':
      return colors.brand;
    case 'atendidasBodega':
      return slaColor(pct);
    case 'tasa':
      return tasaColor(pct);
    default:
      return colors.texto.tertiary;
  }
}
